import { useEffect, useState, type FormEvent } from "react";
import { useI18n, type StringKey } from "../i18n";
import {
  loadDirectory, loadOnboardingTasks, loadOnboardingTemplates, saveOnboardingTemplate, setOnboardingTask, startOnboarding,
  type OnboardingTask, type OnboardingTemplate,
} from "../data";
import type { DirectoryRow, Workspace } from "../types";

const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const OWNERS = ["hr", "manager", "it", "finance", "employee"];

/** Onboarding: new joiners and their checklists, plus the checklist template. */
export function Onboarding({ ws, onOpen }: { ws: Workspace; onOpen: (id: string) => void }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const hr = ws.permissions.has("employees.manage");
  const [tasks, setTasks] = useState<OnboardingTask[] | null>(null);
  const [people, setPeople] = useState<DirectoryRow[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [tab, setTab] = useState<"people" | "template">("people");
  const [startFor, setStartFor] = useState("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const refresh = () => loadOnboardingTasks(cid).then(setTasks).catch((e) => setMsg({ ok: false, text: e.message }));
  useEffect(() => { void refresh(); void loadDirectory(cid).then(setPeople).catch(() => setPeople([])); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  const now = today();
  const byPerson = new Map<string, OnboardingTask[]>();
  for (const x of tasks ?? []) byPerson.set(x.employment_id, [...(byPerson.get(x.employment_id) ?? []), x]);
  const person = (id: string) => people.find((p) => p.employment_id === id);
  const rows = [...byPerson.entries()].map(([id, ts]) => ({ id, ts, done: ts.filter((x) => x.done_at).length, overdue: ts.filter((x) => !x.done_at && x.due_date < now).length }))
    .filter((r) => person(r.id)?.status !== "terminated")
    .sort((a, b) => (person(b.id)?.hire_date ?? "").localeCompare(person(a.id)?.hire_date ?? ""));
  const notStarted = people.filter((p) => (p.status === "starting" || p.status === "probation") && !byPerson.has(p.employment_id));

  async function run(fn: () => Promise<unknown>, done?: string) {
    setMsg(null);
    try { await fn(); await refresh(); if (done) setMsg({ ok: true, text: done }); }
    catch (e) { setMsg({ ok: false, text: errText(e, t("errorGeneric")) }); }
  }

  return (
    <div className="page">
      {hr && (
        <div className="tabs" role="tablist">
          {([["people", "obPeople"], ["template", "obTemplate"]] as ["people" | "template", StringKey][]).map(([k, l]) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "tab current" : "tab"} onClick={() => setTab(k)}>{t(l)}</button>
          ))}
        </div>
      )}
      {msg && <p className={`alert ${msg.ok ? "info" : "danger"}`} role="status">{msg.text}</p>}
      {tab === "template" ? <Template ws={ws} /> : (
        <>
          <section className="card">
            <h2>{t("obNewJoiners")}</h2>
            {tasks === null ? <p className="muted">{t("loading")}</p> : rows.length === 0 ? <p className="muted">{t("obNone")}</p> : (
              <ul className="req-list">
                {rows.map((r) => {
                  const p = person(r.id);
                  return [
                    <li key={r.id}>
                      <span><button type="button" className="link strong" onClick={() => setOpen(open === r.id ? null : r.id)}>{p ? pick(p.full_name, p.full_name_ar) : "—"}</button>
                        <span className="muted small-note"> · {p ? pick(p.job_title ?? "", p.job_title_ar) : ""} · {t("obStarts")} {p?.hire_date}</span></span>
                      <span className="row">
                        <span className="meter" style={{ width: 90 }}><span style={{ width: `${(100 * r.done) / r.ts.length}%` }} /></span>
                        <span className="mono small-note">{r.done}/{r.ts.length}</span>
                        {r.overdue > 0 && <span className="badge danger">{r.overdue} {t("obOverdue")}</span>}
                      </span>
                    </li>,
                    open === r.id && (
                      <li key={`${r.id}-tasks`} className="ob-tasks">
                        <ul className="plain-list">
                          {r.ts.map((x) => (
                            <li key={x.id} className="row spread">
                              <label className="check"><input type="checkbox" checked={!!x.done_at} onChange={(e) => void run(() => setOnboardingTask(x.id, e.target.checked))} /> {pick(x.title_en, x.title_ar)}</label>
                              <span className="muted small-note">{t(`xOwner_${x.owner}` as StringKey)} · <span className={!x.done_at && x.due_date < now ? "danger-text mono" : "mono"}>{x.due_date}</span></span>
                            </li>
                          ))}
                        </ul>
                        <button type="button" className="link" onClick={() => onOpen(r.id)}>{t("obOpenProfile")}</button>
                      </li>
                    ),
                  ];
                })}
              </ul>
            )}
          </section>
          {hr && notStarted.length > 0 && (
            <section className="card">
              <h2>{t("obNotStarted")}</h2>
              <div className="row">
                <select aria-label={t("employee")} value={startFor} onChange={(e) => setStartFor(e.target.value)}>
                  <option value="">—</option>{notStarted.map((p) => <option key={p.employment_id} value={p.employment_id}>{pick(p.full_name, p.full_name_ar)} · {p.hire_date}</option>)}
                </select>
                <button type="button" className="btn primary small" disabled={!startFor} onClick={() => run(() => startOnboarding(startFor), t("obStarted")).then(() => setStartFor(""))}>{t("obStart")}</button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

function Template({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const cid = ws.membership.company_id;
  const [rows, setRows] = useState<OnboardingTemplate[]>([]);
  const [f, setF] = useState({ title_en: "", title_ar: "", owner: "hr", offset: "0", fromProbation: false });
  const refresh = () => loadOnboardingTemplates(cid).then(setRows);
  useEffect(() => { void refresh(); }, [cid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function add(e: FormEvent) {
    e.preventDefault();
    await saveOnboardingTemplate(cid, { title_en: f.title_en, title_ar: f.title_ar || null, owner: f.owner, due_offset: Number(f.offset), from_probation_end: f.fromProbation, sort_order: (rows.length + 1) * 10 + 5 });
    setF({ ...f, title_en: "", title_ar: "" });
    await refresh();
  }
  const when = (x: OnboardingTemplate) => x.from_probation_end
    ? t("obDueProbation").replace("{n}", String(Math.abs(x.due_offset)))
    : x.due_offset < 0 ? t("obDueBefore").replace("{n}", String(-x.due_offset)) : x.due_offset === 0 ? t("obDueFirstDay") : t("obDueAfter").replace("{n}", String(x.due_offset));
  return (
    <>
      <section className="card">
        <h2>{t("obTemplate")}</h2>
        <ul className="req-list">
          {rows.map((x) => (
            <li key={x.id}>
              <span>{pick(x.title_en, x.title_ar)} <span className="muted small-note">· {t(`xOwner_${x.owner}` as StringKey)} · {when(x)}</span></span>
              <label className="check"><input type="checkbox" checked={x.active} onChange={async (e) => { await saveOnboardingTemplate(cid, { id: x.id, active: e.target.checked }); await refresh(); }} /> {t("active")}</label>
            </li>
          ))}
        </ul>
        <p className="muted small-note">{t("obTemplateHelp")}</p>
      </section>
      <form className="card" onSubmit={add}>
        <h2>{t("obAddStep")}</h2>
        <div className="field-row">
          <label className="field"><span>{t("nameEn")}</span><input value={f.title_en} onChange={(e) => setF({ ...f, title_en: e.target.value })} required /></label>
          <label className="field"><span>{t("nameAr")}</span><input dir="rtl" value={f.title_ar} onChange={(e) => setF({ ...f, title_ar: e.target.value })} /></label>
          <label className="field"><span>{t("obOwner")}</span>
            <select value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })}>{OWNERS.map((o) => <option key={o} value={o}>{t(`xOwner_${o}` as StringKey)}</option>)}</select></label>
          <label className="field"><span>{t("obDays")}</span><input type="number" min="-60" max="365" value={f.offset} onChange={(e) => setF({ ...f, offset: e.target.value })} /></label>
        </div>
        <label className="check"><input type="checkbox" checked={f.fromProbation} onChange={(e) => setF({ ...f, fromProbation: e.target.checked })} /> {t("obFromProbation")}</label>
        <div className="actions"><button className="btn secondary">{t("payAdd")}</button></div>
      </form>
    </>
  );
}

/** Self-service: my onboarding checklist (my own steps can be ticked). */
export function MyOnboarding({ ws }: { ws: Workspace }) {
  const { t, pick } = useI18n();
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const refresh = () => ws.membership.employment_id ? loadOnboardingTasks(ws.membership.company_id, ws.membership.employment_id).then(setTasks).catch(() => setTasks([])) : Promise.resolve();
  useEffect(() => { void refresh(); }, [ws.membership.employment_id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (tasks.length === 0 || tasks.every((x) => x.done_at)) return null;
  return (
    <section className="card">
      <h2>{t("obMine")}</h2>
      <ul className="plain-list">
        {tasks.map((x) => (
          <li key={x.id} className="row spread">
            <label className="check"><input type="checkbox" checked={!!x.done_at} disabled={x.owner !== "employee"} onChange={async (e) => { await setOnboardingTask(x.id, e.target.checked); await refresh(); }} /> {pick(x.title_en, x.title_ar)}</label>
            <span className="muted small-note">{t(`xOwner_${x.owner}` as StringKey)} · {x.due_date}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
